import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Card, DataTable, Field, Input, PageHeader, Select, Spinner } from "@empite/ui-components";
import type { UserSummary } from "../admin-client";
import { useUsers } from "../admin-hooks";
import { ErrorAlert, RequireOperator } from "./guards";
import { StatusBadge, when } from "./common";

/**
 * One page of results, deliberately.
 *
 * This is a search an operator reads on the way to one account, not an
 * export: a console that will happily render ten thousand rows is a console
 * somebody uses to copy the customer list.
 */
const PAGE = 50;

const STATUSES = ["active", "suspended", "deleted"];

/**
 * The developer filter as the Select sees it.
 *
 * A `<select>` value is a string and the filter is a tri-state, so the empty
 * string is the third state rather than a fourth control: "" is "do not
 * filter", and it has to survive the round trip to the URL and back without
 * ever becoming `false` — "every account" and "every account that is not a
 * developer" are different searches.
 */
const DEVELOPER_CHOICES: { value: string; label: string }[] = [
  { value: "", label: "All users" },
  { value: "true", label: "Developers only" },
  { value: "false", label: "Non-developers" },
];

/** "" | "true" | "false" -> the boolean the API filter wants, or undefined. */
function developerChoiceToFilter(choice: string): boolean | undefined {
  return choice === "" ? undefined : choice === "true";
}

/** The inverse, for pulling the control back out of a restored URL. */
function developerFilterToChoice(developer: boolean | undefined): string {
  return developer === undefined ? "" : String(developer);
}

export interface UsersSearch {
  q?: string;
  status?: string;
  /** Tri-state: absent means both kinds. See DEVELOPER_CHOICES. */
  developer?: boolean;
}

/**
 * Users — the search box, and nothing until it is used.
 *
 * The query lives in the URL rather than in component state so an operator
 * can paste "the search I was looking at" into a ticket, and so the back
 * button returns to the same results instead of an empty box.
 */
export function Users({ search }: { search: UsersSearch }) {
  return <RequireOperator>{() => <UserSearch search={search} />}</RequireOperator>;
}

function UserSearch({ search }: { search: UsersSearch }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(search.q ?? "");
  const [status, setStatus] = useState(search.status ?? "");
  const [developer, setDeveloper] = useState(developerFilterToChoice(search.developer));

  // The URL is the source of truth, so a back or forward navigation has to
  // pull the inputs back to whatever it restored.
  useEffect(() => {
    setQ(search.q ?? "");
    setStatus(search.status ?? "");
    setDeveloper(developerFilterToChoice(search.developer));
  }, [search.q, search.status, search.developer]);

  const users = useUsers({
    q: search.q,
    status: search.status,
    developer: search.developer,
    limit: PAGE,
  });
  const rows = users.data?.users ?? [];

  return (
    <>
      <PageHeader
        title="Users"
        description="Find an account by name, email or id, then work on its devices and sessions."
      />
      <Card>
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            // Submitted rather than searched-as-you-type: every keystroke
            // would be a query against the customer table, and a search that
            // fires on "a" is a search that reads the whole account list.
            void navigate({
              to: "/users",
              search: {
                q: q.trim() || undefined,
                status: status || undefined,
                developer: developerChoiceToFilter(developer),
              },
            });
          }}
        >
          <Field label="Search" htmlFor="q" hint="Name, email address or user id.">
            <Input id="q" value={q} onChange={(event) => setQ(event.target.value)} />
          </Field>
          <Field label="Status" htmlFor="status">
            <Select id="status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">Any status</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Developer" htmlFor="developer">
            <Select
              id="developer"
              value={developer}
              onChange={(event) => setDeveloper(event.target.value)}
            >
              {DEVELOPER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" busy={users.isFetching}>
            Search
          </Button>
        </form>
      </Card>
      <ErrorAlert error={users.error} title="Could not search the users" />
      <Card title="Results">
        {users.isPending ? (
          <Spinner label="Searching" />
        ) : (
          <DataTable<UserSummary>
            rows={rows}
            rowKey={(row) => row.id}
            empty="No account matches that search."
            columns={[
              {
                key: "name",
                header: "Account",
                render: (row) => (
                  <>
                    <Link to="/users/$userId" params={{ userId: row.id }}>
                      {row.display_name || row.primary_email || row.id}
                    </Link>
                    <div className="esm-muted-text">{row.primary_email}</div>
                  </>
                ),
              },
              {
                key: "status",
                header: "Status",
                // The developer marker rides in the status cell rather than in
                // a column of its own: almost every row is blank there, and a
                // near-empty column costs the width the account and email need.
                // It stays a StatusBadge — neutral tone, because green and red
                // in this table mean "the account is healthy" and "the account
                // is dead", and a developer flag is neither.
                render: (row) => (
                  <div className="esm-badge-row">
                    <StatusBadge status={row.status} />
                    {row.is_empite_developer ? <StatusBadge status="developer" /> : null}
                  </div>
                ),
              },
              {
                key: "registration",
                header: "Registration",
                render: (row) => <code>{row.registration_state}</code>,
              },
              {
                key: "phone",
                header: "Phone",
                // Last digits only, everywhere. The platform stores the number
                // encrypted and no admin route returns a readable one.
                render: (row) => (row.phone_last_digits ? `••• ${row.phone_last_digits}` : "—"),
              },
              { key: "created", header: "Joined", render: (row) => when(row.created_at) },
              { key: "last", header: "Last sign-in", render: (row) => when(row.last_login_at) },
            ]}
          />
        )}
      </Card>
    </>
  );
}
